import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import MobileFixedCTA from '../../components/feature/MobileFixedCTA';
import HeroSection from './components/HeroSection';
import OverviewBadges from './components/OverviewBadges';
import FlowChart from './components/FlowChart';
import StepSection from './components/StepSection';
import DocumentsSection from './components/DocumentsSection';
import RequirementsSection from './components/RequirementsSection';
import FaqSection from './components/FaqSection';
import CtaSection from './components/CtaSection';
import RelatedLinksSection from './components/RelatedLinksSection';
import { usePageSEO } from '../../hooks/usePageSEO';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { createArticleStructuredData, createHowToStructuredData, createFAQStructuredData, createBreadcrumbStructuredData } from '../../utils/structuredData';

export default function AccountOpening() {
  // SEO設定
  usePageSEO({
    title: 'DMM FXの口座開設手順を画像付きで解説 | 最短5分で完了',
    description: 'DMM FXの口座開設手順を画像付きで詳しく解説。最短5分で申込完了、最短即日で取引開始可能。必要書類、審査基準、スマホで簡単本人確認の方法まで初心者にもわかりやすく説明します。',
    keywords: 'DMM FX,口座開設,手順,必要書類,本人確認,審査,マイナンバー,最短即日',
    structuredData: [
      createArticleStructuredData({
        headline: 'DMM FXの口座開設手順を画像付きで解説',
        description: 'DMM FXの口座開設手順を画像付きで詳しく解説。最短5分で申込完了、最短即日で取引開始可能です。',
        author: 'DMM FX情報局',
        datePublished: '2024-10-20',
        dateModified: new Date().toISOString().split('T')[0],
        image: 'https://public.readdy.ai/ai/img_res/df0d304cbd6e80bba89375fb655d21a9.jpg',
        url: '/account-opening',
      }),
      createHowToStructuredData({
        name: 'DMM FX口座開設の手順',
        description: 'DMM FXの口座を開設する方法を4つのステップで解説します。',
        totalTime: 'PT5M',
        estimatedCost: {
          currency: 'JPY',
          value: '0',
        },
        steps: [
          {
            name: '口座開設申込',
            text: 'DMM FX公式サイトから口座開設を申し込みます。メールアドレス、氏名、住所などの基本情報を入力。最短5分で完了します。',
            image: 'https://readdy.ai/api/search-image?query=online%20form%20application%20registration%20input%20fields%20modern%20clean%20interface&width=800&height=600&seq=step1-form-account&orientation=landscape',
          },
          {
            name: '本人確認書類の提出',
            text: 'スマホで本人確認書類（運転免許証やマイナンバーカード）を撮影してアップロード。マイナンバー書類も必要です。',
            image: 'https://readdy.ai/api/search-image?query=document%20verification%20id%20card%20upload%20smartphone%20scan%20modern%20interface&width=800&height=600&seq=step2-document-account&orientation=landscape',
          },
          {
            name: '審査・口座開設完了',
            text: 'DMM FXによる審査が行われます。最短即日で審査完了し、メールでログイン情報が届きます。',
            image: 'https://readdy.ai/api/search-image?query=approval%20confirmation%20email%20notification%20success%20checkmark%20modern%20clean&width=800&height=600&seq=step3-approval-account&orientation=landscape',
          },
          {
            name: '入金して取引開始',
            text: 'クイック入金で即時反映。手数料無料で入金できます。デモ口座で練習してから本番取引を始めましょう。',
            image: 'https://readdy.ai/api/search-image?query=deposit%20money%20transfer%20bank%20payment%20trading%20start%20modern%20interface&width=800&height=600&seq=step4-deposit-account&orientation=landscape',
          },
        ],
      }),
      createFAQStructuredData([
        {
          question: '口座開設にどのくらい時間がかかりますか？',
          answer: '申込自体は最短5分で完了します。審査は最短即日で完了し、その日のうちに取引を始められる場合もあります。',
        },
        {
          question: '口座開設に必要な書類は？',
          answer: '本人確認書類（運転免許証、マイナンバーカードなど）とマイナンバー確認書類が必要です。スマホで撮影してアップロードできます。',
        },
        {
          question: '口座開設の審査基準は？',
          answer: '20歳以上75歳未満、日本国内在住、金融資産が100万円以上などの条件があります。詳細は公式サイトでご確認ください。',
        },
        {
          question: '口座開設や維持に費用はかかりますか？',
          answer: '口座開設費用、口座維持費用は一切かかりません。完全無料で口座を開設・維持できます。',
        },
      ]),
      createBreadcrumbStructuredData([
        { name: 'ホーム', url: '/' },
        { name: '口座開設手順', url: '/account-opening' },
      ]),
    ],
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* パンくずリスト */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <Breadcrumb items={[
            { name: 'ホーム', url: '/' },
            { name: 'FX初心者ガイド', url: '/fx-guide' },
            { name: '口座開設手順', url: '/account-opening' }
          ]} />
        </div>
      </div>
      
      <HeroSection />
      <OverviewBadges />
      <FlowChart />
      <StepSection />
      <DocumentsSection />
      <RequirementsSection />
      <FaqSection />
      <CtaSection />
      <RelatedLinksSection />
      <Footer />
      
      {/* スマホ用固定CTAボタン */}
      <MobileFixedCTA />
      
      {/* 固定CTAの高さ分の余白（スマホのみ） */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}
